import {analytics} from '@cl/shared';
import {CancelToken} from 'axios';
import apiV2Service from 'common/src/modules/api/api-v2.service';
import enumsService from 'common/src/modules/enums/enums.service';
import {CLEnumType} from 'common/src/modules/enums/enums.types';
import _filter from 'lodash/filter';
import _isNil from 'lodash/isNil';
import _map from 'lodash/map';
import _mapValues from 'lodash/mapValues';
import _some from 'lodash/some';
import {DateTime} from 'luxon';
import {externalInsightsMetrics} from '../campaignAnalytics/campaignAnalytics.constants';
import dataSourcesApi from '../dataSources/dataSources.api';
import {DataSourceType} from '../dataSources/dataSources.types';
import {TimezoneObject} from '../timezones/timezones.types';
import getTimeZoneStringForDateTime from '../timezones/utils/getTimezoneStringForDateTime';
import {ANALYTICS_LIMIT, REMOTE_METRICS_GROUP_BY, advertiserMetrics} from './analytics.constants';
import {
  toClientAnalyticsTotalsWithTrends,
  toClientCampaignsMetric,
  toClientDimensionPossibleValues,
  toClientOptionalDimensions,
  toClientOptionalDimensionsForFilters,
  toClientOptionalInsightsDimensions,
  toClientOptionalMetrics,
  toClientOptionsForDimensionValue,
  toClientSavedViewData,
  toRemoteFilters,
} from './analytics.dataConverters';
import {
  AnalyticsData,
  AnalyticsEventType,
  AnalyticsFilterItem,
  AnalyticsTotalsWithTrends,
  AnalyticsUiNames,
  CampaignMetric,
  DateRange,
  Dimension,
  DimensionValue,
  InsightsMetricOptions,
  InsightsMetricSavedViewsOptionParams,
  MetricAndDimensionOptions,
  MetricValue,
  MetricsGroupBy,
  MetricsValue,
  RemoteAnalyticsData,
  RemoteAnalyticsTotalsWithTrends,
  RemoteAnalyticsUiNames,
  RemoteCampaignMetric,
  RemoteInsightsMetricSavedViewsOption,
  RemoteOptionalDimensions,
  RemoteOptionalMetrics,
} from './analytics.types';
import extractCustomConversionsFromMetrics from './utils/extractCustomConversionsFromMetrics';
import getFiltersQuery from './utils/getFiltersQuery';

const {cachedAnalyticsSubject} = analytics;

const BASE_PATH = 'Analytics';

async function getSavedViews() {
  const {data} = await apiV2Service.executeGetRequest<RemoteInsightsMetricSavedViewsOption[]>(
    `${BASE_PATH}/GetViews`,
  );

  return toClientSavedViewData(data);
}

async function getOptionalDimensionsForFilters() {
  const {data} = await apiV2Service.executeGetRequest<RemoteOptionalDimensions[]>(
    `${BASE_PATH}/GetOptionalFilters`,
  );

  return toClientOptionalDimensionsForFilters(data);
}

async function getOptionalMetrics({
  customMetricsOptions,
  hasMetaCustomConversions,
}: {
  hasMetaCustomConversions: boolean;
  customMetricsOptions?: Partial<Record<MetricValue, string[]>>;
}) {
  const [{data}, hasExternalAggregatedData] = await Promise.all([
    apiV2Service.executeGetRequest<RemoteOptionalMetrics[]>(`${BASE_PATH}/GetOptionalMetrics`),
    dataSourcesApi
      .getDataSources()
      .then((dataSources) => _some(dataSources, {type: DataSourceType.ExternalAggregatedData})),
  ]);

  const metrics = hasExternalAggregatedData
    ? data
    : _filter(data, ({value}) => !externalInsightsMetrics.includes(value));

  return toClientOptionalMetrics({
    data: metrics,
    hasMetaCustomConversions,
    customMetricsOptions,
  });
}

async function getOptionalDimensions(
  customMetricsOptions?: Partial<Record<MetricValue, string[]>>,
) {
  const {data} = await apiV2Service.executeGetRequest<RemoteOptionalDimensions[]>(
    `${BASE_PATH}/GetOptionalViewBy`,
  );

  return toClientOptionalDimensions(data, customMetricsOptions);
}

async function deleteSavedViews(viewId: number) {
  return await apiV2Service.executeDeleteRequest<string>(`${BASE_PATH}/DeleteView/${viewId}`);
}

async function addSavedView({
  dimensions,
  filters,
  viewName,
  graphStyle,
  metrics,
  dateRange: {endDate, startDate},
  groupBy,
  eventType,
}: InsightsMetricSavedViewsOptionParams) {
  const [customConversions, allMetrics] = extractCustomConversionsFromMetrics(metrics);

  return await apiV2Service.executePostRequest<number>(
    `${BASE_PATH}/AddView`,
    {},
    {
      filterUi: {
        metrics: allMetrics,
        ...(customConversions.length ? {customConversions} : {}),
        dimensions,
        toExclusive: endDate.toISO(),
        fromInclusive: startDate.toISO(),
        ...(!!filters?.length && {filters: getFiltersQuery(filters)}),
        eventType,
      },
      metricsGroupBy: groupBy,
      viewName,
      graphStyle,
      viewBy: dimensions.flat(),
    },
  );
}

async function getOptionalFiltersByDimension(
  dimension: string,
  campaignId?: number,
  nodeId?: number,
  eventType?: AnalyticsEventType,
) {
  const {data: filterValues} = await apiV2Service.executeGetRequest<string[]>(
    `${BASE_PATH}/GetFilterOptinalValues`,
    {
      dimension,
      ...(campaignId ? {[DimensionValue.CampaignID]: campaignId} : {}),
      ...(nodeId ? {[DimensionValue.NodeId]: nodeId} : {}),
      ...(eventType ? {[DimensionValue.EventType]: eventType} : {}),
    },
  );

  return filterValues;
}

async function getAnalyticsTotals({
  metrics,
  filters,
  dateRange: {startDate, endDate},
  selectedTimezone,
  cancelToken,
  dimensions = [],
  eventType,
}: {
  metrics: MetricValue[];
  filters: AnalyticsFilterItem[];
  dateRange: DateRange;
  selectedTimezone: TimezoneObject;
  cancelToken?: CancelToken;
  dimensions?: DimensionValue[];
  eventType: AnalyticsEventType;
}): Promise<MetricsValue> {
  const timezone = getTimeZoneStringForDateTime(selectedTimezone);
  const [customConversions, allMetrics] = extractCustomConversionsFromMetrics(metrics);

  return apiV2Service
    .executePostRequest<MetricsValue>(
      `${BASE_PATH}/totals`,
      {},
      {
        metrics: allMetrics,
        dimensions,
        ...(customConversions.length ? {customConversions} : {}),
        ...(!!filters?.length && {filters: toRemoteFilters(filters)}),
        toExclusive: endDate.setZone(timezone, {keepLocalTime: true}).toISO(),
        fromInclusive: startDate.setZone(timezone, {keepLocalTime: true}).toISO(),
        ...(eventType ? {[DimensionValue.EventType]: eventType} : {}),
      },
      {cancelToken},
    )
    .then(({data}) => data);
}

async function getAnalyticsData({
  dimensions,
  filters,
  metrics,
  dateRange: {endDate, startDate},
  topPerforming,
  limit: count,
  selectedTimezone,
  groupBy = MetricsGroupBy.Total,
  cancelToken,
  useConversionTime = false,
  eventType,
}: {
  metrics: MetricValue[];
  dimensions: DimensionValue[];
  dateRange: DateRange;
  filters?: AnalyticsFilterItem[];
  topPerforming?: boolean;
  limit?: number;
  selectedTimezone: TimezoneObject;
  groupBy?: MetricsGroupBy;
  cancelToken?: CancelToken;
  useConversionTime?: boolean;
  eventType: AnalyticsEventType | null;
}): Promise<AnalyticsData[]> {
  const timezone = getTimeZoneStringForDateTime(selectedTimezone);
  const [customConversions, allMetrics] = extractCustomConversionsFromMetrics(metrics);
  const [dimension] = dimensions;

  const payload = {
    count,
    metrics: allMetrics,
    ...(customConversions.length ? {customConversions} : {}),
    dimensions: dimensions,
    viewBy: dimension,
    toExclusive: endDate.setZone(timezone, {keepLocalTime: true}).toISO(),
    fromInclusive: startDate.setZone(timezone, {keepLocalTime: true}).toISO(),
    ...(!!filters?.length && {filters: getFiltersQuery(filters)}),
    ...(groupBy !== MetricsGroupBy.Total && {groupBy: REMOTE_METRICS_GROUP_BY[groupBy]}),
    ...(!_isNil(useConversionTime) && {useConversionTime}),
    ...(eventType ? {[DimensionValue.EventType]: eventType} : {}),
  };

  if (!count) delete payload.count;
  const apiCallPath = topPerforming ? 'TopPerforming' : 'GetAnalytics';

  const {data} = await apiV2Service.executePostRequest<RemoteAnalyticsData[]>(
    `${BASE_PATH}/${apiCallPath}`,
    {},
    payload,
    {cancelToken},
  );

  return data;
}

async function getDashboardWidgetData(params: {
  metrics: MetricValue[];
  dimensions: DimensionValue[];
  dateRange: DateRange;
  filters?: AnalyticsFilterItem[];
  limit?: number;
  selectedTimezone: TimezoneObject;
  topPerforming?: boolean;
  cancelToken?: CancelToken;
  eventType: AnalyticsEventType | null;
}) {
  const [dimension] = params.dimensions;

  const [analyticsData, dimensionValues] = await Promise.all([
    getAnalyticsData(params),
    getOptionsForDimensionValues(dimension),
  ]);

  return {analyticsData, dimensionValues};
}

async function getCampaignsMetrics({
  campaignMetrics,
  dateRange,
  selectedTimezone,
}: {
  campaignMetrics: MetricValue[];
  dateRange: {
    startDate: DateTime;
    endDate: DateTime;
  };
  selectedTimezone: TimezoneObject;
}): Promise<CampaignMetric[]> {
  const timezone = getTimeZoneStringForDateTime(selectedTimezone);
  const {data} = await apiV2Service.executePostRequest<RemoteCampaignMetric[]>(
    `${BASE_PATH}/GetCampaignsMetrics`,
    {},
    {
      metrics: campaignMetrics,
      toExclusive: dateRange.endDate.setZone(timezone, {keepLocalTime: true}).toISO(),
      fromInclusive: dateRange.startDate.setZone(timezone, {keepLocalTime: true}).toISO(),
    },
  );

  return _map(data, toClientCampaignsMetric);
}

async function getTopPerformingData({
  dimensions,
  filters,
  metrics,
  dateRange: {endDate, startDate},
  count,
  offset,
  selectedTimezone,
  groupBy = MetricsGroupBy.Total,
  searchTerm = '',
  orderBy = '',
  orderByDesc = true,
  apiForMultipleMetrics,
  cancelToken,
  eventType,
}: {
  metrics: MetricValue[];
  dimensions: DimensionValue[];
  dateRange: DateRange;
  filters?: AnalyticsFilterItem[];
  topPerforming?: boolean;
  count?: number;
  offset?: number;
  selectedTimezone: TimezoneObject;
  groupBy?: MetricsGroupBy;
  searchTerm?: string;
  orderBy?: string;
  orderByDesc?: boolean;
  apiForMultipleMetrics?: boolean;
  cancelToken?: CancelToken;
  eventType: AnalyticsEventType | null;
}) {
  const timezone = getTimeZoneStringForDateTime(selectedTimezone);
  const [customConversions, allMetrics] = extractCustomConversionsFromMetrics(metrics);

  const payload = {
    count,
    offset,
    searchTerm,
    orderBy,
    orderByDesc,
    metrics: allMetrics,
    ...(customConversions.length ? {customConversions} : {}),
    dimensions: dimensions,
    viewBy: [dimensions].flat().join(','),
    toExclusive: endDate.setZone(timezone, {keepLocalTime: true}).toISO(),
    fromInclusive: startDate.setZone(timezone, {keepLocalTime: true}).toISO(),
    ...(!!filters?.length && {filters: getFiltersQuery(filters)}),
    ...(groupBy !== MetricsGroupBy.Total && {groupBy: REMOTE_METRICS_GROUP_BY[groupBy]}),
    ...(eventType ? {[DimensionValue.EventType]: eventType} : {}),
  };

  if (!count) delete payload.count;
  if (!offset) delete payload.offset;

  const {data, meta, pagination} = await apiV2Service.executePostRequest<RemoteAnalyticsData[]>(
    `${BASE_PATH}/${apiForMultipleMetrics ? 'GetDataForDashboard' : 'TopPerforming'}`,
    {},
    payload,
    {cancelToken},
  );

  return {items: data, meta, pagination};
}

async function getOptionalMetricsAndDimensions(
  hasMetaCustomConversions: boolean,
): Promise<MetricAndDimensionOptions> {
  try {
    const customMetricsOptions = {
      [MetricValue.CustomConversions]: _map(
        cachedAnalyticsSubject.data.optionsByMetricsKey[MetricValue.CustomConversions],
        'value',
      ),
    };

    const [metrics, dimensions] = await Promise.all([
      getOptionalMetrics({customMetricsOptions, hasMetaCustomConversions}),
      getOptionalDimensions(customMetricsOptions),
    ]);

    return {metrics, dimensions};
  } catch (e) {
    const [metrics, dimensions] = await Promise.all([
      getOptionalMetrics({hasMetaCustomConversions}),
      getOptionalDimensions(),
    ]);

    return {metrics, dimensions};
  }
}

async function getMetricsOptionsForSelector(
  hasMetaCustomConversions: boolean,
): Promise<InsightsMetricOptions> {
  const [{metrics, dimensions}, savedViewOptions] = await Promise.all([
    getOptionalMetricsAndDimensions(hasMetaCustomConversions),
    getSavedViews(),
  ]);

  return {
    metricsOptions: metrics,
    savedViewOptions,
    dimensionsOptions: dimensions,
  };
}

async function getOptionsForDimensionValues(dimension: DimensionValue) {
  const isDimensionWithPossibleValues = [
    DimensionValue.Device,
    DimensionValue.CampaignType,
  ].includes(dimension);

  const [campaignTypeOptionalValues, deviceOptionalValues] = await Promise.all([
    enumsService.loadEnums(CLEnumType.CampaignPage).then(toClientOptionsForDimensionValue),
    isDimensionWithPossibleValues
      ? apiV2Service
          .executeGetRequest<Record<string, DimensionValue>>(
            `${BASE_PATH}/GetDimensionPossibleValues`,
            {
              dimension,
            },
          )
          .then(({data}) => (data ? toClientDimensionPossibleValues(data) : {}))
          .catch(() => {})
      : Promise.resolve({}),
  ]);

  return {...deviceOptionalValues, ...campaignTypeOptionalValues} as Dimension;
}

async function getAdvertiserMetrics({
  dateRange: {startDate, endDate},
  selectedTimezone,
}: {
  dateRange: DateRange;
  selectedTimezone: TimezoneObject;
}) {
  const timezone = getTimeZoneStringForDateTime(selectedTimezone);

  const {data} = await apiV2Service.executePostRequest<Record<MetricValue, number>[]>(
    `${BASE_PATH}/GetAdvertiserMetrics`,
    {},
    {
      metrics: advertiserMetrics,
      toExclusive: endDate.setZone(timezone, {keepLocalTime: true}).toISO(),
      fromInclusive: startDate.setZone(timezone, {keepLocalTime: true}).toISO(),
    },
  );

  return data;
}

async function getUiNames(): Promise<AnalyticsUiNames> {
  const {data} = await apiV2Service.executeGetRequest<RemoteAnalyticsUiNames>(
    `${BASE_PATH}/GetUiNames`,
  );

  const dimensions: Record<DimensionValue, string> = _mapValues(data.dimensions, 'name') as Record<
    DimensionValue,
    string
  >;

  return {
    ...data,
    dimensions,
    dimensionUiMetadataMap: data.dimensions,
  };
}

async function getAnalyticsTotalsWithTrends({
  metrics,
  filters,
  dateRange: {startDate, endDate},
  selectedTimezone,
  cancelToken,
  eventType,
}: {
  metrics: MetricValue[];
  filters: AnalyticsFilterItem[];
  dateRange: DateRange;
  selectedTimezone: TimezoneObject;
  cancelToken?: CancelToken;
  eventType: AnalyticsEventType;
}): Promise<AnalyticsTotalsWithTrends> {
  const timezone = getTimeZoneStringForDateTime(selectedTimezone);
  const [customConversions, allMetrics] = extractCustomConversionsFromMetrics(metrics);

  const {data} = await apiV2Service.executePostRequest<RemoteAnalyticsTotalsWithTrends>(
    `${BASE_PATH}/TotalsWithTrends`,
    {},
    {
      metrics: allMetrics,
      ...(customConversions.length ? {customConversions} : {}),
      ...(!!filters?.length && {filters: toRemoteFilters(filters)}),
      toExclusive: endDate.setZone(timezone, {keepLocalTime: true}).toISO(),
      fromInclusive: startDate.setZone(timezone, {keepLocalTime: true}).toISO(),
      ...(eventType ? {[DimensionValue.EventType]: eventType} : {}),
    },
    {cancelToken},
  );

  return toClientAnalyticsTotalsWithTrends(data);
}

async function getCampaignOptionalViewBy() {
  const {data} = await apiV2Service.executeGetRequest<RemoteOptionalDimensions[]>(
    `${BASE_PATH}/GetCampaignOptionalViewBy`,
  );

  return toClientOptionalInsightsDimensions(data);
}

async function getCampaignInsightsAnalytics({
  dimensions,
  filters,
  metrics,
  dateRange: {endDate, startDate},
  limit: count,
  selectedTimezone,
  groupBy = MetricsGroupBy.Total,
  campaignId,
  sourceNodeId,
  topPerforming = false,
  offset,
}: {
  campaignId: number;
  sourceNodeId: number;
  metrics: MetricValue[];
  dimensions: DimensionValue[];
  dateRange: DateRange;
  filters?: AnalyticsFilterItem[];
  topPerforming?: boolean;
  limit?: number;
  selectedTimezone: TimezoneObject;
  groupBy?: MetricsGroupBy;
  offset?: number;
}): Promise<AnalyticsData[]> {
  const timezone = getTimeZoneStringForDateTime(selectedTimezone);
  const [customConversions, allMetrics] = extractCustomConversionsFromMetrics(metrics);
  const [dimension] = dimensions;

  const payload = {
    count,
    campaignId,
    ...(!!sourceNodeId ? {sourceNodeId} : {}),
    metrics: allMetrics,
    ...(customConversions.length ? {customConversions} : {}),
    dimensions,
    viewBy: dimension,
    offset,
    toExclusive: endDate.setZone(timezone, {keepLocalTime: true}).toISO(),
    fromInclusive: startDate.setZone(timezone, {keepLocalTime: true}).toISO(),
    ...(!!filters?.length && {filters: getFiltersQuery(filters)}),
    ...(groupBy !== MetricsGroupBy.Total && {groupBy: REMOTE_METRICS_GROUP_BY[groupBy]}),
  };

  if (!count) delete payload.count;

  const {data} = await apiV2Service.executePostRequest<RemoteAnalyticsData[]>(
    `${BASE_PATH}/${
      topPerforming ? 'CampaignInsightsTopPerforming' : 'GetCampaignInsightsAnalytics'
    }`,
    {},
    payload,
  );

  return data;
}

async function getDataForDashboard<T>({
  dateRange: {endDate, startDate},
  metrics,
  dimensions,
  filters,
  selectedTimezone,
  withTrends,
  count = ANALYTICS_LIMIT,
  cancelToken,
  eventType,
}: {
  metrics: MetricValue[];
  dimensions: DimensionValue[];
  dateRange: DateRange;
  filters?: AnalyticsFilterItem[];
  selectedTimezone: TimezoneObject;
  withTrends: boolean;
  count?: number;
  cancelToken?: CancelToken;
  // TODO DIMA: remove this once we have a proper event type
  eventType: AnalyticsEventType;
}) {
  const timezone = getTimeZoneStringForDateTime(selectedTimezone);
  const [customConversions, allMetrics] = extractCustomConversionsFromMetrics(metrics);

  const payload = {
    metrics: allMetrics,
    ...(customConversions.length ? {customConversions} : {}),
    dimensions: dimensions,
    viewBy: [dimensions].flat().join(','),
    toExclusive: endDate.setZone(timezone, {keepLocalTime: true}).toISO(),
    fromInclusive: startDate.setZone(timezone, {keepLocalTime: true}).toISO(),
    ...(!!filters?.length && {filters: getFiltersQuery(filters)}),
    count,
    ...(eventType ? {[DimensionValue.EventType]: eventType} : {}),
  };

  const {data} = await apiV2Service.executePostRequest<T[]>(
    `${BASE_PATH}/${withTrends ? 'GetDataForDashboardWithTrends' : 'GetDataForDashboard'}`,
    {},
    payload,
    {cancelToken},
  );

  return data;
}

async function getWorstPerformingData({
  dimensions,
  filters,
  metrics: metricsWithCustomConversions,
  dateRange: {endDate, startDate},
  count,
  offset,
  selectedTimezone,
  groupBy = MetricsGroupBy.Total,
  orderBy = '',
  cancelToken,
  eventType,
}: {
  metrics: MetricValue[];
  dimensions: DimensionValue[];
  dateRange: DateRange;
  filters?: AnalyticsFilterItem[];
  topPerforming?: boolean;
  count?: number;
  offset?: number;
  selectedTimezone: TimezoneObject;
  groupBy?: MetricsGroupBy;
  orderBy?: string;
  cancelToken?: CancelToken;
  eventType: AnalyticsEventType | null;
}) {
  const timezone = getTimeZoneStringForDateTime(selectedTimezone);
  const [customConversions, metrics] = extractCustomConversionsFromMetrics(
    metricsWithCustomConversions,
  );

  const payload = {
    count,
    offset,
    orderBy,
    metrics,
    ...(customConversions.length ? {customConversions} : {}),
    dimensions: dimensions,
    viewBy: [dimensions].flat().join(','),
    toExclusive: endDate.setZone(timezone, {keepLocalTime: true}).toISO(),
    fromInclusive: startDate.setZone(timezone, {keepLocalTime: true}).toISO(),
    ...(!!filters?.length && {filters: getFiltersQuery(filters)}),
    ...(groupBy !== MetricsGroupBy.Total && {groupBy: REMOTE_METRICS_GROUP_BY[groupBy]}),
    ...(eventType ? {[DimensionValue.EventType]: eventType} : {}),
  };

  if (!count) delete payload.count;
  if (!offset) delete payload.offset;

  const {data, meta, pagination} = await apiV2Service.executePostRequest<RemoteAnalyticsData[]>(
    `${BASE_PATH}/WorstPerforming`,
    {},
    payload,
    {cancelToken},
  );

  return {items: data, meta, pagination};
}

export default {
  addSavedView,
  getSavedViews,
  getAnalyticsData,
  deleteSavedViews,
  getAnalyticsTotals,
  getCampaignsMetrics,
  getTopPerformingData,
  getDashboardWidgetData,
  getOptionalFiltersByDimension,
  getOptionalDimensionsForFilters,
  getOptionalMetricsAndDimensions,
  getMetricsOptionsForSelector,
  getOptionsForDimensionValues,
  getOptionalMetrics,
  getAdvertiserMetrics,
  getAnalyticsTotalsWithTrends,
  getCampaignOptionalViewBy,
  getCampaignInsightsAnalytics,
  getUiNames,
  getDataForDashboard,
  getWorstPerformingData,
};
